import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, FileText, Users, Building2 } from "lucide-react";
import { format } from "date-fns";

export default function ParsingSettings() {
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: parsingJobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['parsing-jobs', refreshKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parsing_jobs')
        .select(`
          *,
          candidates(first_name, last_name, email_primary),
          jobs(title)
        `)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data;
    }
  });

  const { data: skillAliases } = useQuery({
    queryKey: ['skill-aliases', refreshKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('skill_aliases')
        .select('*, skills(name)')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data;
    }
  });

  const { data: companyAliases } = useQuery({
    queryKey: ['company-aliases', refreshKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_aliases')
        .select('*, companies(name)')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data;
    }
  });

  const { data: parsingSettings } = useQuery({
    queryKey: ['parsing-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .like('setting_key', 'parsing_%');
      
      if (error) throw error;
      return data;
    }
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing': return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'pending': return <Clock className="h-4 w-4 text-muted-foreground" />;
      default: return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      completed: "default",
      failed: "destructive",
      processing: "secondary",
      pending: "outline"
    };
    return <Badge variant={variants[status] || "outline"}>{status}</Badge>;
  };

  const stats = {
    total: parsingJobs?.length || 0,
    completed: parsingJobs?.filter(j => j.status === 'completed').length || 0,
    failed: parsingJobs?.filter(j => j.status === 'failed').length || 0,
    processing: parsingJobs?.filter(j => j.status === 'processing').length || 0,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Parsing Settings</h1>
          <p className="text-muted-foreground">Monitor resume parsing jobs and manage deduplication settings</p>
        </div>
        <Button variant="outline" onClick={() => setRefreshKey(k => k + 1)}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-600">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-600">Processing</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.processing}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="jobs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="jobs">
            <FileText className="h-4 w-4 mr-2" />
            Parsing Jobs
          </TabsTrigger>
          <TabsTrigger value="skill-aliases">
            <Users className="h-4 w-4 mr-2" />
            Skill Aliases ({skillAliases?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="company-aliases">
            <Building2 className="h-4 w-4 mr-2" />
            Company Aliases ({companyAliases?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="jobs">
          <Card>
            <CardHeader>
              <CardTitle>Recent Parsing Jobs</CardTitle>
              <CardDescription>Monitor resume parsing status and results</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Job</TableHead>
                      <TableHead>Phase</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Stats</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobsLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                      </TableRow>
                    ) : parsingJobs?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No parsing jobs found
                        </TableCell>
                      </TableRow>
                    ) : (
                      parsingJobs?.map((job) => (
                        <TableRow key={job.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(job.status)}
                              {getStatusBadge(job.status)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {job.candidates?.first_name} {job.candidates?.last_name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {job.candidates?.email_primary}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{job.jobs?.title || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {job.current_phase || 'pending'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary transition-all" 
                                  style={{ width: `${job.progress_percent || 0}%` }}
                                />
                              </div>
                              <span className="text-xs">{job.progress_percent || 0}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {job.extraction_stats && (
                              <div className="text-xs space-y-0.5">
                                <div>Exp: {(job.extraction_stats as any).experience_count || 0}</div>
                                <div>Skills: {(job.extraction_stats as any).skills_count || 0}</div>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(job.created_at), 'MMM d, HH:mm')}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="skill-aliases">
          <Card>
            <CardHeader>
              <CardTitle>Skill Aliases</CardTitle>
              <CardDescription>Mappings from raw skill names to canonical skills (auto-created during parsing)</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Raw Name</TableHead>
                      <TableHead>Canonical Skill</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {skillAliases?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                          No skill aliases yet. They will be created automatically during resume parsing.
                        </TableCell>
                      </TableRow>
                    ) : (
                      skillAliases?.map((alias) => (
                        <TableRow key={alias.id}>
                          <TableCell className="font-mono text-sm">{alias.raw_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{(alias.skills as any)?.name}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {format(new Date(alias.created_at), 'MMM d, yyyy')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="company-aliases">
          <Card>
            <CardHeader>
              <CardTitle>Company Aliases</CardTitle>
              <CardDescription>Mappings from raw company names to canonical companies</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Raw Name</TableHead>
                      <TableHead>Canonical Company</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companyAliases?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                          No company aliases yet. They will be created automatically during resume parsing.
                        </TableCell>
                      </TableRow>
                    ) : (
                      companyAliases?.map((alias) => (
                        <TableRow key={alias.id}>
                          <TableCell className="font-mono text-sm">{alias.raw_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{(alias.companies as any)?.name}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {format(new Date(alias.created_at), 'MMM d, yyyy')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
